import React, { useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, TextField,
  InputAdornment, CircularProgress, Alert, ToggleButtonGroup, ToggleButton,
  Divider
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import DataTable from 'react-data-table-component'
import { getActiveOrders, getOrdersByRestaurant, getStaffPayouts, getStaffMember } from '../apollo'

const GET_ACTIVE_ORDERS = gql`${getActiveOrders}`
const GET_ORDERS = gql`${getOrdersByRestaurant}`
const GET_STAFF_PAYOUTS = gql`${getStaffPayouts}`
const GET_STAFF_MEMBER = gql`${getStaffMember}`

const statusColor = {
  PENDING: { bg: '#fef9c3', color: '#ca8a04' },
  ACCEPTED: { bg: '#eff6ff', color: '#3b82f6' },
  ASSIGNED: { bg: '#f3e8ff', color: '#8b5cf6' },
  PICKED: { bg: '#fef3c7', color: '#d97706' },
  DELIVERED: { bg: '#f0fdf4', color: '#16a34a' },
  CANCELLED: { bg: '#fef2f2', color: '#ef4444' },
  PREPARED: { bg: '#ecfdf5', color: '#059669' },
}

function TipsSummary({ staffId }) {
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_MEMBER, {
    variables: { id: staffId },
    skip: !staffId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: payoutsData, loading: payoutsLoading } = useQuery(GET_STAFF_PAYOUTS, {
    variables: { staffId },
    skip: !staffId,
    fetchPolicy: 'cache-and-network',
  })

  const staff = staffData?.staffMember
  const payouts = payoutsData?.staffPayouts ?? []

  const totalEarned = parseFloat(staff?.totalTipsEarned || 0)
  const totalPaid = parseFloat(staff?.totalTipsPaid || 0)
  const outstanding = Math.max(0, totalEarned - totalPaid)

  const recentPayouts = [...payouts].sort((a, b) => new Date(b.paidAt) - new Date(a.paidAt)).slice(0, 5)

  if (staffLoading || payoutsLoading) {
    return <Box mb={3}><CircularProgress size={20} /></Box>
  }

  return (
    <Paper elevation={0} sx={{ mb: 3, borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AttachMoneyIcon sx={{ color: '#16a34a', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>My Tips</Typography>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: 'flex', px: 3, py: 2.5, gap: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em', mb: 0.3 }}>TOTAL EARNED</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#16a34a' }}>${totalEarned.toFixed(2)}</Typography>
        </Box>
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9' }} />
        <Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em', mb: 0.3 }}>PAID OUT</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#3b82f6' }}>${totalPaid.toFixed(2)}</Typography>
        </Box>
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9' }} />
        <Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em', mb: 0.3 }}>OUTSTANDING</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: outstanding > 0 ? '#f59e0b' : '#94a3b8' }}>
            ${outstanding.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Recent payouts */}
      {recentPayouts.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', mb: 1.5 }}>
              RECENT PAYOUTS
            </Typography>
            {recentPayouts.map(p => (
              <Box key={p._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid #f8fafc' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                    ${parseFloat(p.amount).toFixed(2)} · {p.method}
                  </Typography>
                  {p.note && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{p.note}</Typography>}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  )
}

function ServerOrders() {
  const raw = localStorage.getItem('staff-session')
  const session = raw ? JSON.parse(raw) : {}
  const restaurantId = session.restaurantId
  const staffId = session.userId ? String(session.userId) : null

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [viewFilter, setViewFilter] = useState('mine') // 'mine' | 'all'

  const { data: activeData, loading: activeLoading, error: activeError } = useQuery(GET_ACTIVE_ORDERS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })

  const { data, loading, error } = useQuery(GET_ORDERS, {
    variables: { restaurant: restaurantId, page, rows: 50, search: search.length > 2 ? search : '' },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })

  // Active orders assigned to me
  const myActiveOrders = (activeData?.getActiveOrders || []).filter(o =>
    o.assignedStaff?._id === staffId
  )

  // All recent orders (table)
  const allRecentOrders = data?.ordersByRestId ?? []
  const myRecentOrders = allRecentOrders.filter(o => o.assignedStaff?._id === staffId)

  const displayedOrders = viewFilter === 'mine' ? myRecentOrders : allRecentOrders

  const columns = [
    {
      name: 'ORDER', sortable: true, selector: r => r.orderId, minWidth: '100px',
      cell: r => <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b' }}>#{r.orderId}</Typography>
    },
    {
      name: 'CUSTOMER', hide: 'md', minWidth: '160px',
      cell: r => (
        <Box>
          <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#1e293b' }}>{r.user?.name || '—'}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.user?.phone || ''}</Typography>
        </Box>
      )
    },
    {
      name: 'AMOUNT', selector: r => r.orderAmount, minWidth: '100px',
      cell: r => <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>${parseFloat(r.orderAmount || 0).toFixed(2)}</Typography>
    },
    {
      name: 'SERVER TIP', hide: 'sm', minWidth: '100px',
      cell: r => r.serverTip > 0
        ? <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#16a34a' }}>${parseFloat(r.serverTip).toFixed(2)}</Typography>
        : <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>—</Typography>
    },
    {
      name: 'STATUS', minWidth: '130px',
      cell: r => {
        const s = statusColor[r.orderStatus] || { bg: '#f1f5f9', color: '#64748b' }
        return <Chip label={r.orderStatus} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.7rem' }} />
      }
    },
    {
      name: 'DATE', hide: 'md', minWidth: '130px', selector: r => r.createdAt,
      cell: r => <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</Typography>
    },
  ]

  const customStyles = {
    headCells: { style: { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', backgroundColor: '#f8fafc' } },
    rows: { style: { borderBottom: '1px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } } },
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ReceiptLongIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>My Orders</Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{session.restaurantName}</Typography>
        </Box>
      </Box>

      {(error || activeError) && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{(error || activeError).message}</Alert>}

      {/* Tips summary panel */}
      {staffId && <TipsSummary staffId={staffId} />}

      {/* Active orders assigned to me */}
      {myActiveOrders.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#111827', mb: 1 }}>
            Active Orders ({myActiveOrders.length})
          </Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {myActiveOrders.map(o => {
              const s = statusColor[o.orderStatus] || { bg: '#f1f5f9', color: '#64748b' }
              return (
                <Paper key={o._id} elevation={0} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, minWidth: 160 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b' }}>#{o.orderId}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.5 }}>{o.user?.name || '—'}</Typography>
                  <Chip label={o.orderStatus} size="small" sx={{ mt: 0.75, bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
                </Paper>
              )
            })}
          </Box>
        </Box>
      )}
      {activeLoading && myActiveOrders.length === 0 && (
        <Box mb={2}><CircularProgress size={20} /></Box>
      )}

      {/* Order history table */}
      <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <ToggleButtonGroup
            value={viewFilter}
            exclusive
            onChange={(_, v) => v && setViewFilter(v)}
            size="small"
          >
            <ToggleButton value="mine" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
              My Orders ({myRecentOrders.length})
            </ToggleButton>
            <ToggleButton value="all" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
              All Orders
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField size="small" placeholder="Search orders..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
        </Box>

        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <DataTable
            columns={columns}
            data={displayedOrders}
            customStyles={customStyles}
            noHeader
            pagination
            paginationServer
            paginationTotalRows={displayedOrders.length < 50 ? (page - 1) * 50 + displayedOrders.length : page * 50 + 1}
            onChangePage={p => setPage(p)}
            paginationPerPage={50}
            noDataComponent={
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {viewFilter === 'mine' ? 'No orders assigned to you yet' : 'No orders found'}
                </Typography>
              </Box>
            }
          />
        )}
      </Paper>
    </Container>
  )
}

export default ServerOrders
